/**
 * Missile exhaust FX — a hot teal/gold trail streaming from a flying missile
 * (family `missiles`). *Core decides, Phaser renders*: core flies the missile body
 * along its diagonal; this puffs an exhaust plume out of its tail each frame so the
 * streak reads with speed. Purely decorative; one shared emitter, no per-id state.
 */
import Phaser from 'phaser';
import type { CircleMoverHazard } from '../core';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

const FX_DEPTH = 4; // just behind the missile body (hazards draw at 5)

export class MissileFx {
  private readonly scene: Phaser.Scene;
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Puff exhaust at the missile's tail (opposite its travel direction). */
  emit(h: CircleMoverHazard): void {
    const e = this.ensure();
    const ang = Math.atan2(h.to.y - h.from.y, h.to.x - h.from.x);
    const tx = h.x - Math.cos(ang) * h.r;
    const ty = h.y - Math.sin(ang) * h.r;
    e.emitParticleAt(tx, ty, 2);
  }

  reset(): void {
    this.emitter?.killAll();
  }

  destroy(): void {
    this.emitter?.destroy();
    this.emitter = undefined;
  }

  private ensure(): Phaser.GameObjects.Particles.ParticleEmitter {
    if (!this.emitter) {
      this.emitter = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 180, max: 430 },
          speed: { min: 6, max: 44 },
          scale: { start: 0.5, end: 0, ease: 'quad.out' },
          alpha: { start: 0.85, end: 0, ease: 'quad.in' },
          // Hot gold core fading through teal exhaust.
          color: [HAZARD_COLORS.missileGold, HAZARD_COLORS.missileBody, HAZARD_COLORS.blastTeal],
          colorEase: 'quad.out',
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        })
        .setDepth(FX_DEPTH);
      this.emitter.reserve(140);
    }
    return this.emitter;
  }
}
